import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dumbbell, Newspaper, Trophy, Camera, Crown, Target, Users, Flame,
  Heart, MessageCircle, Send, ChevronDown, BarChart3, Bell, CalendarDays,
  Zap, UserPlus, TrendingUp, X, ArrowLeft
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const EVENT_CONFIG = {
  training_join: { icon: Dumbbell, color: 'text-cyan-400', bg: 'bg-cyan-900/30', label: 'Entreno' },
  entrenamiento_fuerza: { icon: Dumbbell, color: 'text-cyan-400', bg: 'bg-cyan-900/30', label: 'Fuerza' },
  record_broken: { icon: Flame, color: 'text-orange-400', bg: 'bg-orange-900/30', label: 'Record' },
  badge_earned: { icon: Trophy, color: 'text-gold', bg: 'bg-yellow-900/30', label: 'Logro' },
  photo_upload: { icon: Camera, color: 'text-green-400', bg: 'bg-green-900/30', label: 'Foto' },
  news_created: { icon: Newspaper, color: 'text-purple-400', bg: 'bg-purple-900/30', label: 'Noticia' },
  new_pro: { icon: Crown, color: 'text-gold', bg: 'bg-yellow-900/30', label: 'Pro' },
  racha: { icon: Zap, color: 'text-amber-400', bg: 'bg-amber-900/30', label: 'Racha' },
  racha_entreno: { icon: Zap, color: 'text-amber-400', bg: 'bg-amber-900/30', label: 'Racha' },
  ranking: { icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-900/30', label: 'Ranking' },
  nuevo_miembro: { icon: UserPlus, color: 'text-sky-400', bg: 'bg-sky-900/30', label: 'Bienvenida' },
  checkin: { icon: Target, color: 'text-indigo-400', bg: 'bg-indigo-900/30', label: 'Check-in' },
  checkin_completed: { icon: Target, color: 'text-indigo-400', bg: 'bg-indigo-900/30', label: 'Check-in' },
  resumen_semanal: { icon: BarChart3, color: 'text-gray-400', bg: 'bg-gray-800/50', label: 'Resumen' },
  estadistica: { icon: BarChart3, color: 'text-gray-400', bg: 'bg-gray-800/50', label: 'Stats' },
  comunidad: { icon: Users, color: 'text-gray-400', bg: 'bg-gray-800/50', label: 'Klan' },
  recordatorio: { icon: CalendarDays, color: 'text-gray-400', bg: 'bg-gray-800/50', label: 'Info' },
};

const FILTERS = [
  { key: 'todo', label: 'Todo' },
  { key: 'entreno', label: 'Entrenos' },
  { key: 'record', label: 'Records' },
  { key: 'logro', label: 'Logros' },
  { key: 'social', label: 'Social' },
];

function timeAgo(ts) {
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true, locale: es });
  } catch { return ''; }
}

// --- Comment Section ---
function CommentSection({ eventId, count, onCountChange }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feed/${eventId}/comments`);
      setComments(res.data);
    } catch {}
  }, [eventId]);

  useEffect(() => { if (open) loadComments(); }, [open, loadComments]);

  const submit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/feed/${eventId}/comment`, { texto: text });
      setComments(prev => [...prev, res.data]);
      setText('');
      onCountChange(1);
    } catch {} finally { setSending(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-gray-400 hover:text-gray-200 transition-colors text-xs"
        data-testid={`btn-comments-${eventId}`}>
        <MessageCircle className="w-3.5 h-3.5" />
        <span>{count || 0}</span>
      </button>
    );
  }

  return (
    <div className="mt-3 space-y-2" data-testid={`comments-section-${eventId}`}>
      <button onClick={() => setOpen(false)} className="text-xs text-gray-400 hover:text-gray-300">Ocultar comentarios</button>
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {comments.map(c => (
          <div key={c.id} className="flex gap-2 items-start">
            <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0 mt-0.5">
              {c.user_avatar ? (
                <img src={`${BACKEND_URL}${c.user_avatar}`} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-[9px] text-gray-400">{(c.user_nombre || '?')[0]}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs"><span className="font-semibold text-white">{c.user_nombre}</span>{' '}
                <span className="text-gray-400">{c.texto}</span></p>
              <p className="text-[10px] text-gray-400">{timeAgo(c.created_at)}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 items-center">
        <Input value={text} onChange={e => setText(e.target.value)} placeholder="Escribe un comentario..."
          className="bg-gray-800/50 border-gray-700 text-white text-xs h-8 flex-1"
          onKeyDown={e => e.key === 'Enter' && submit()} data-testid={`comment-input-${eventId}`} />
        <button onClick={submit} disabled={sending || !text.trim()}
          className="text-gold hover:text-gold/80 disabled:text-gray-600 transition-colors" data-testid={`comment-send-${eventId}`}>
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// --- Single Feed Event ---
function FeedEvent({ item, currentUserId }) {
  const cfg = EVENT_CONFIG[item.type] || EVENT_CONFIG.training_join;
  const Icon = cfg.icon;
  const isSistema = item.es_sistema;
  const isRecord = item.type === 'record_broken';
  const [likes, setLikes] = useState(item.likes || []);
  const [commCount, setCommCount] = useState(item.comentarios_count || 0);
  const liked = likes.some(l => l.user_id === currentUserId);

  const toggleLike = async () => {
    try {
      const res = await axios.post(`${API}/feed/${item.id}/like`);
      setLikes(res.data.likes);
    } catch {}
  };

  if (isSistema) {
    return (
      <div className="flex gap-3 items-start px-3 py-3 rounded-xl bg-gray-900/40 border border-dashed border-gray-800/60"
        data-testid={`feed-event-sistema-${item.id}`}>
        <div className="w-8 h-8 rounded-full bg-gray-800/60 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Icon className="w-3.5 h-3.5 text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-400">{item.details}</p>
          <p className="text-[10px] text-gray-400 mt-1">{timeAgo(item.timestamp)} &middot; {cfg.label}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`px-3 py-3 rounded-xl transition-colors ${isRecord ? 'bg-gold/5 border border-gold/20' : 'bg-gray-900 border border-gray-800'}`}
      data-testid={`feed-event-${item.id}`}>
      <div className="flex gap-3 items-start">
        <div className={`w-9 h-9 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0 border border-white/5`}>
          {item.user_avatar ? (
            <img src={`${BACKEND_URL}${item.user_avatar}`} alt={item.user_name} className="w-full h-full rounded-full object-cover" />
          ) : (
            <Icon className={`w-4 h-4 ${cfg.color}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-200">
            <span className="font-bold text-white">{item.user_name}</span>{' '}
            {item.details}
          </p>
          {item.metadata?.image && (
            <div className="mt-2 rounded-lg overflow-hidden h-24 w-full max-w-[200px] border border-gray-700">
              <img src={`${BACKEND_URL}${item.metadata.image}`} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex items-center gap-4 mt-2">
            <p className="text-[10px] text-gray-400">{timeAgo(item.timestamp)} &middot; {cfg.label}</p>
            <button onClick={toggleLike} className={`flex items-center gap-1 text-xs transition-colors p-1.5 -m-1.5 rounded-lg ${liked ? 'text-red-400' : 'text-gray-400 hover:text-red-400'}`}
              data-testid={`btn-like-${item.id}`}>
              <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
              <span>{likes.length || ''}</span>
            </button>
            <CommentSection eventId={item.id} count={commCount} onCountChange={delta => setCommCount(c => c + delta)} />
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Day Group Header ---
function DayHeader({ label }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="h-px flex-1 bg-gray-800" />
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</span>
      <div className="h-px flex-1 bg-gray-800" />
    </div>
  );
}

function groupByDay(items) {
  const now = new Date();
  const today = now.toDateString();
  const yesterday = new Date(now - 86400000).toDateString();
  const groups = {};
  items.forEach(item => {
    const d = new Date(item.timestamp);
    let label;
    if (d.toDateString() === today) label = 'Hoy';
    else if (d.toDateString() === yesterday) label = 'Ayer';
    else {
      const diff = Math.floor((now - d) / 86400000);
      label = diff <= 7 ? 'Esta semana' : 'Anteriores';
    }
    if (!groups[label]) groups[label] = [];
    groups[label].push(item);
  });
  return groups;
}

// --- Main Feed Page ---
export default function MuroKlan() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todo');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef(null);

  const fetchFeed = useCallback(async (reset = false) => {
    try {
      const o = reset ? 0 : offset;
      const res = await axios.get(`${API}/feed?limit=30&offset=${o}&categoria=${filter}`);
      if (reset) {
        setItems(res.data);
        setOffset(30);
      } else {
        setItems(prev => [...prev, ...res.data]);
        setOffset(o + 30);
      }
      setHasMore(res.data.length >= 30);
    } catch (err) {
      console.error('Error fetching feed:', err);
    } finally { setLoading(false); }
  }, [filter, offset]);

  useEffect(() => {
    setLoading(true);
    setItems([]);
    setOffset(0);
    setHasMore(true);
    fetchFeed(true);
  }, [filter]);

  // Poll every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchFeed(true), 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const groups = groupByDay(items);
  const groupOrder = ['Hoy', 'Ayer', 'Esta semana', 'Anteriores'];

  return (
    <div className="min-h-screen bg-dark-mk" data-testid="muro-klan-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-dark-mk/90 backdrop-blur-md border-b border-gray-800/50 px-4 pt-4 pb-3">
        <h1 className="text-xl font-bold text-white flex items-center gap-2 mb-3">
          <Users className="w-5 h-5 text-gold" /> Muro del Klan
        </h1>
        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar" data-testid="feed-filters">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                filter === f.key ? 'bg-gold text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`} data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Feed content */}
      <div className="px-4 pb-24 max-w-lg mx-auto" ref={containerRef}>
        {loading && items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Cargando actividad...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
            <Users className="w-10 h-10" />
            <p className="text-sm">No hay actividad reciente</p>
          </div>
        ) : (
          <>
            {groupOrder.map(label => {
              const group = groups[label];
              if (!group || group.length === 0) return null;
              return (
                <div key={label}>
                  <DayHeader label={label} />
                  <div className="space-y-3">
                    {group.map(item => (
                      <FeedEvent key={item.id} item={item} currentUserId={user?.id} />
                    ))}
                  </div>
                </div>
              );
            })}
            {hasMore && (
              <button onClick={() => fetchFeed(false)}
                className="w-full py-3 mt-4 text-sm text-gray-400 hover:text-gray-200 flex items-center justify-center gap-1 transition-colors"
                data-testid="btn-load-more">
                <ChevronDown className="w-4 h-4" /> Cargar mas
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
